/**
 * Built-in 3D volume rendering color presets (CT, Hounsfield Units).
 *
 * Lives in the visualization layer next to Color3DService so the service can
 * resolve preset ids without importing from the components layer.
 *
 * Pure data — the vtk transfer functions are built from these presets by
 * Color3DService.
 */

import type { RGB } from '../domain';

export interface Color3DOpacityPoint {
    /** Scalar value (HU). */
    value: number;
    /** Opacity in [0, 1]. */
    opacity: number;
}

export interface Color3DColorPoint {
    /** Scalar value (HU). */
    value: number;
    /** RGB components in [0, 1]. */
    r: number;
    g: number;
    b: number;
}

/**
 * Gradient-opacity ramp. vtk.js models gradient opacity as a linear ramp
 * between (minValue, minOpacity) and (maxValue, maxOpacity) — it does not
 * support arbitrary piecewise gradient-opacity functions like desktop VTK.
 */
export interface Color3DGradientOpacityRamp {
    minValue: number;
    minOpacity: number;
    maxValue: number;
    maxOpacity: number;
}

/**
 * Bounds of the window/level adjustment, in HU. Shared by every control that
 * moves it — the drag tool's sensitivity, the 3D Colors slider and the clamp in
 * Color3DService — so they all cover the same range.
 *
 * The level spans the intensities a contrast-filled aorta can occupy; the window
 * may not exceed that whole span, since a colormap wider than the data has no
 * contrast left to show.
 */
export const COLOR_3D_LEVEL_RANGE = { min: 0, max: 800 } as const;
export const COLOR_3D_WINDOW_RANGE = {
    // A zero-width (or inverted) window would collapse the transfer function
    min: 1,
    max: COLOR_3D_LEVEL_RANGE.max - COLOR_3D_LEVEL_RANGE.min,
} as const;

/**
 * Transfer function ported from the desktop TH_TF_AORTA preset, shared by every
 * profile that renders this colormap and differs only in lighting ('default'
 * and 'report'). Keeping it in one place is what makes the "same transfer
 * functions, different material" claim on those presets true by construction.
 *
 * Its window/level is the span of the opacity points (395 - 125) centered on
 * 260 — the reference frame the user's adjustment is applied to.
 */
const AORTA_TRANSFER_FUNCTION = {
    window: 270,
    level: 260,
    opacityPoints: [
        { value: 125, opacity: 0 },
        { value: 395, opacity: 0.8 },
    ],
    colorPoints: [
        { value: 32, r: 0, g: 0, b: 0 },
        { value: 125, r: 0, g: 0, b: 0 },
        { value: 224, r: 1, g: 0.0823529, b: 0.105882 },
        { value: 280, r: 1, g: 0.690196, b: 0.0352941 },
        { value: 337, r: 1, g: 0.945098, b: 0.152941 },
        { value: 395, r: 1, g: 1, b: 1 },
    ],
} as const;

export const COLOR_3D_PRESETS = [
    {
        id: 'default',
        label: 'Default',
        selectable: true,
        ...AORTA_TRANSFER_FUNCTION,
        shade: true,
        ambient: 0.1,
        diffuse: 0.9,
        specular: 0.2,
        specularPower: 10,
        gradientOpacity: null,
        background: null,
    },
    {
        // Ported from the desktop TH_TF_VIRTUAL_ANGIO preset: an all-black,
        // near-transparent volume where vessel walls appear through gradient
        // opacity — mimics a fluoroscopic angiography view.
        id: 'virtual-angio',
        label: 'Virtual Angio',
        window: 410,
        level: 295,
        selectable: true,
        opacityPoints: [
            { value: 90, opacity: 0 },
            { value: 500, opacity: 1 },
        ],
        colorPoints: [
            { value: 90, r: 0, g: 0, b: 0 },
            { value: 250, r: 0, g: 0, b: 0 },
            { value: 500, r: 0, g: 0, b: 0 },
        ],
        shade: false,
        ambient: 0.1,
        diffuse: 0.9,
        specular: 0.2,
        specularPower: 10,
        // Desktop uses points (0, 0.01) → (10, 0.1) → (20, 0.01); vtk.js only
        // supports a monotonic ramp, so we keep the rising edge.
        gradientOpacity: { minValue: 0, minOpacity: 0.01, maxValue: 10, maxOpacity: 0.1 },
        // Fluoroscopy look: dark vessels over a light background
        background: [0.9, 0.9, 0.9],
    },
    {
        // The 'default' preset with flatter lighting for the report's light
        // background. Same transfer function — only the material changes:
        // shading stays on (surfaces keep their shape cue) but most of the light
        // comes from the shadowless ambient term, so the faces turned away from
        // the camera no longer fall into near-black shadow on white paper.
        // ambient + diffuse still sums to 1, so overall brightness is unchanged.
        id: 'report',
        label: 'Report',
        // A pinned rendering profile, not a viewing mode — never offered in the
        // 3D Colors tool
        selectable: false,
        ...AORTA_TRANSFER_FUNCTION,
        shade: true,
        ambient: 0.5,
        diffuse: 0.5,
        // Softer highlight: a specular blowout reads as a white hole on a light page
        specular: 0.1,
        specularPower: 10,
        gradientOpacity: null,
        // Keep the viewport's own (light) background
        background: null,
    },
] as const;

/**
 * Stable preset identifier union — derived from {@link COLOR_3D_PRESETS} so the
 * set of valid ids stays in sync with the presets themselves.
 */
export type Color3DPresetId = (typeof COLOR_3D_PRESETS)[number]['id'];

/** Preset applied when nothing else is configured (and after reset()). */
export const DEFAULT_COLOR_3D_PRESET_ID: Color3DPresetId = 'default';

export interface Color3DPreset {
    /** Stable identifier. */
    id: Color3DPresetId;
    /** Human-readable label. */
    label: string;
    /**
     * Transfer-function window (width) the control points below were authored
     * at. Together with {@link level} it is the reference frame for the
     * window/level adjustment and the value "reset to default" restores.
     */
    window: number;
    /** Transfer-function level (center) the control points were authored at. */
    level: number;
    /** Whether the user may pick this preset from the 3D Colors tool. */
    selectable: boolean;
    /** Scalar opacity transfer function points. */
    opacityPoints: ReadonlyArray<Color3DOpacityPoint>;
    /** Color transfer function points. */
    colorPoints: ReadonlyArray<Color3DColorPoint>;
    /** Whether shading (lighting) is enabled. */
    shade: boolean;
    ambient: number;
    diffuse: number;
    specular: number;
    specularPower: number;
    /** Gradient-opacity ramp, or null to disable gradient opacity. */
    gradientOpacity: Color3DGradientOpacityRamp | null;
    /** Viewport background override, or null to keep the viewport's own background. */
    background: RGB | null;
}

/** Resolve a preset by id, or null if unknown. */
export function getColor3DPreset(id: Color3DPresetId): Color3DPreset | null {
    return COLOR_3D_PRESETS.find((p) => p.id === id) ?? null;
}

/** Presets the user may choose from the 3D Colors tool. */
export function getSelectableColor3DPresets(): Color3DPreset[] {
    return COLOR_3D_PRESETS.filter((p) => p.selectable);
}
